// ./src/hooks/useRunningMessages.ts
import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { OP_RESULT_CODES } from "@sdflc/api-helpers";
import {
  RunningMessage,
  RunningMessageInput,
  RunningMessageFilter,
  RunningMessageWhereInput,
  RunningMessageService,
  Pagination,
} from "@/utils/api";
import { SortConfig } from "@/components/generic";
import { DEFAULT_PAGE_SIZE } from "@/config";

export interface UseRunningMessagesOptions {
  /** Initial page size for pagination */
  pageSize?: number;

  /** Initial filters */
  initialFilters?: Partial<RunningMessageFilter>;

  /** Initial sort configuration */
  initialSort?: SortConfig[];

  /** Auto-load data on mount */
  autoLoad?: boolean;
}

export interface UseRunningMessagesReturn {
  // Data state
  runningMessage: RunningMessage | null;
  runningMessages: RunningMessage[];
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  isEmpty: boolean;

  // Search and filters
  filters: Partial<RunningMessageFilter>;
  sortBy: SortConfig[];

  // Pagination
  currentPage: number;
  pageSize: number;

  // Operations state
  isCreating: boolean;
  isUpdating: boolean;
  removingMessageId: string | null;
  selectedMessage: RunningMessage | null;

  // Core operations (matching API method names exactly)
  get: (id: string) => Promise<void>;
  list: (
    filter?: Partial<RunningMessageFilter>,
    pagination?: Pagination,
    sortBy?: SortConfig[],
    reset?: boolean
  ) => Promise<void>;
  getMany: (ids: string[]) => Promise<RunningMessage[]>;
  create: (params: RunningMessageInput) => Promise<RunningMessage | null>;
  update: (
    where: RunningMessageWhereInput,
    params: RunningMessageInput
  ) => Promise<RunningMessage | null>;
  remove: (where: RunningMessageWhereInput) => Promise<boolean>;
  removeMany: (where: RunningMessageWhereInput[]) => Promise<boolean>;

  // Additional convenience methods
  loadMore: () => Promise<void>;
  refresh: () => Promise<void>;

  // Filter operations
  setFilters: (filters: Partial<RunningMessageFilter>) => void;
  setSortBy: (sort: SortConfig[]) => void;

  // UI state management
  setSelectedMessage: (message: RunningMessage | null) => void;

  // Helper operations
  reset: () => void;

  // Computed values
  inactiveMessages: RunningMessage[];
  messagesByVenue: Record<string, RunningMessage[]>;
  messagesByStatus: Record<number, RunningMessage[]>;

  // Error handling
  lastError: string | null;
  clearError: () => void;
}

const DEFAULT_SORT: SortConfig[] = [{ key: "name", direction: "asc" }];

export const useRunningMessages = (
  options: UseRunningMessagesOptions = {}
): UseRunningMessagesReturn => {
  const {
    pageSize = DEFAULT_PAGE_SIZE,
    initialFilters = {},
    initialSort = DEFAULT_SORT,
    autoLoad = false,
  } = options;

  // Use refs for option values to avoid dependency issues
  const pageSizeRef = useRef(pageSize);
  const sortByRef = useRef<SortConfig[]>(initialSort);

  // Update refs when options change
  useEffect(() => {
    pageSizeRef.current = pageSize;
  }, [pageSize]);

  // Data state
  const [runningMessage, setRunningMessage] = useState<RunningMessage | null>(
    null
  );
  const [runningMessages, setRunningMessages] = useState<RunningMessage[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);

  // Search and filters
  const [filters, setFilters] =
    useState<Partial<RunningMessageFilter>>(initialFilters);
  const [sortBy, setSortBy] = useState<SortConfig[]>(initialSort);

  // Update sortByRef when sortBy state changes
  useEffect(() => {
    sortByRef.current = sortBy;
  }, [sortBy]);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);

  // Operations state
  const [isCreating, setIsCreating] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [removingMessageId, setRemovingMessageId] = useState<string | null>(
    null
  );
  const [selectedMessage, setSelectedMessage] = useState<RunningMessage | null>(
    null
  );

  // Error handling
  const [lastError, setLastError] = useState<string | null>(null);

  // Computed values
  const isEmpty = useMemo(
    () => runningMessages.length === 0 && !loading,
    [runningMessages.length, loading]
  );

  const inactiveMessages = useMemo(() => {
    return runningMessages.filter((message) => message.status !== 100);
  }, [runningMessages]);

  const messagesByVenue = useMemo(() => {
    return runningMessages.reduce((acc, message) => {
      const venueId = message.venueId || "unknown";
      if (!acc[venueId]) {
        acc[venueId] = [];
      }
      acc[venueId].push(message);
      return acc;
    }, {} as Record<string, RunningMessage[]>);
  }, [runningMessages]);

  const messagesByStatus = useMemo(() => {
    return runningMessages.reduce((acc, message) => {
      const status = message.status || 0;
      if (!acc[status]) {
        acc[status] = [];
      }
      acc[status].push(message);
      return acc;
    }, {} as Record<number, RunningMessage[]>);
  }, [runningMessages]);

  // Helper function to update message in list if it exists, or add it if not
  const updateOrAddMessageInList = useCallback(
    (updatedMessage: RunningMessage) => {
      setRunningMessages((prev) => {
        const existingIndex = prev.findIndex((m) => m.id === updatedMessage.id);
        if (existingIndex >= 0) {
          const newMessages = [...prev];
          newMessages[existingIndex] = updatedMessage;
          return newMessages;
        } else {
          return [updatedMessage, ...prev];
        }
      });
    },
    []
  );

  // Helper function to remove message from list if it exists
  const removeMessageFromList = useCallback((messageId: string) => {
    setRunningMessages((prev) => prev.filter((m) => m.id !== messageId));
  }, []);

  // Clear error function
  const clearError = useCallback(() => {
    setLastError(null);
  }, []);

  // Reset function
  const reset = useCallback(() => {
    setRunningMessage(null);
    setRunningMessages([]);
    setLastError(null);
    setLoading(false);
    setLoadingMore(false);
    setHasMore(false);
    setCurrentPage(1);
    setIsCreating(false);
    setIsUpdating(false);
    setRemovingMessageId(null);
    setSelectedMessage(null);
  }, []);

  // Get single running message (matching API method name)
  const get = useCallback(
    async (id: string) => {
      setLoading(true);
      setLastError(null);

      try {
        const result = await RunningMessageService.get(id);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const messageData = result.data[0] as RunningMessage;
          setRunningMessage(messageData);
          updateOrAddMessageInList(messageData);
          setLastError(null);
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to load running message";
          setLastError(errorMessage);
          setRunningMessage(null);
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to load running message";
        setLastError(errorMessage);
        setRunningMessage(null);
      } finally {
        setLoading(false);
      }
    },
    [updateOrAddMessageInList]
  );

  // List running messages (matching API method name) - NO dependencies
  const list = useCallback(
    async (
      filter: Partial<RunningMessageFilter> = {},
      pagination?: Pagination,
      sortByParams?: SortConfig[],
      reset: boolean = false
    ) => {
      const targetPage = pagination?.page || 1;
      const targetPageSize = pagination?.pageSize || pageSizeRef.current;
      const isAppending = !reset;

      if (isAppending) {
        setLoadingMore(true);
      } else {
        setLoading(true);
        if (reset) {
          setRunningMessages([]);
          setCurrentPage(1);
        }
      }

      setFilters(filter);
      if (sortByParams) setSortBy(sortByParams);

      const currentSortBy = sortByParams || sortByRef.current;

      try {
        const paginationParams: Pagination = {
          page: targetPage,
          pageSize: targetPageSize,
        };

        const sortParams = currentSortBy.map((sort) => ({
          name: sort.key,
          order: sort.direction.toUpperCase() as "ASC" | "DESC",
        }));

        const result = await RunningMessageService.list(
          filter as RunningMessageFilter,
          paginationParams,
          sortParams
        );

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const newMessages = Array.isArray(result.data) ? result.data : [];

          if (isAppending) {
            setRunningMessages((prev) => {
              const existingIds = new Set(prev.map((m) => m.id));
              const uniqueNewMessages = newMessages.filter(
                (m) => !existingIds.has(m.id)
              );
              return [...prev, ...uniqueNewMessages];
            });
          } else {
            setRunningMessages(newMessages);
          }

          setHasMore(newMessages.length === targetPageSize);
          setCurrentPage(targetPage);
          setLastError(null);
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] ||
            "Failed to load running messages";
          setLastError(errorMessage);
          if (!isAppending) setRunningMessages([]);
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to load running messages";
        setLastError(errorMessage);
        if (!isAppending) setRunningMessages([]);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    []
  );

  // Get many running messages by IDs (matching API method name)
  const getMany = useCallback(
    async (ids: string[]): Promise<RunningMessage[]> => {
      if (ids.length === 0) {
        return [];
      }

      setLoading(true);
      setLastError(null);

      try {
        const result = await RunningMessageService.getMany(ids);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const loadedMessages = Array.isArray(result.data) ? result.data : [];
          loadedMessages.forEach((message) => {
            updateOrAddMessageInList(message);
          });
          setLastError(null);
          return loadedMessages;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] ||
            "Failed to load running messages";
          setLastError(errorMessage);
          return [];
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to load running messages";
        setLastError(errorMessage);
        return [];
      } finally {
        setLoading(false);
      }
    },
    [updateOrAddMessageInList]
  );

  // Create running message (matching API method name)
  const create = useCallback(
    async (params: RunningMessageInput): Promise<RunningMessage | null> => {
      setIsCreating(true);
      setLastError(null);

      try {
        const result = await RunningMessageService.create(params);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const newMessage = result.data[0] as RunningMessage;
          setRunningMessage(newMessage);
          setRunningMessages((prev) => [newMessage, ...prev]);
          setLastError(null);
          return newMessage;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] ||
            "Failed to create running message";
          setLastError(errorMessage);
          return null;
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to create running message";
        setLastError(errorMessage);
        return null;
      } finally {
        setIsCreating(false);
      }
    },
    []
  );

  // Update running message (matching API method name)
  const update = useCallback(
    async (
      where: RunningMessageWhereInput,
      params: RunningMessageInput
    ): Promise<RunningMessage | null> => {
      setIsUpdating(true);
      setLastError(null);

      try {
        const result = await RunningMessageService.update(where.id, params);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const updatedMessage = result.data[0] as RunningMessage;
          setRunningMessage(updatedMessage);
          updateOrAddMessageInList(updatedMessage);
          setLastError(null);
          return updatedMessage;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] ||
            "Failed to update running message";
          setLastError(errorMessage);
          return null;
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to update running message";
        setLastError(errorMessage);
        return null;
      } finally {
        setIsUpdating(false);
      }
    },
    [updateOrAddMessageInList]
  );

  // Remove running message (matching API method name)
  const remove = useCallback(
    async (where: RunningMessageWhereInput): Promise<boolean> => {
      const messageId = where.id;
      if (!messageId) {
        setLastError("Message ID is required for removal");
        return false;
      }

      setRemovingMessageId(messageId);
      setLastError(null);

      try {
        const result = await RunningMessageService.remove(messageId);

        if (result.code === OP_RESULT_CODES.OK) {
          setRunningMessage(null);
          removeMessageFromList(messageId);
          setLastError(null);
          return true;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] ||
            "Failed to remove running message";
          setLastError(errorMessage);
          return false;
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to remove running message";
        setLastError(errorMessage);
        return false;
      } finally {
        setRemovingMessageId(null);
      }
    },
    [removeMessageFromList]
  );

  // Remove many running messages (matching API method name)
  const removeMany = useCallback(
    async (where: RunningMessageWhereInput[]): Promise<boolean> => {
      setLastError(null);

      try {
        const messageIds = where.map((w) => w.id).filter(Boolean) as string[];

        if (messageIds.length === 0) {
          setLastError("No message IDs provided for removal");
          return false;
        }

        const result = await RunningMessageService.removeMany(messageIds);

        if (result.code === OP_RESULT_CODES.OK) {
          setRunningMessage(null);
          setRunningMessages((prev) =>
            prev.filter((m) => !messageIds.includes(m.id!))
          );
          setLastError(null);
          return true;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] ||
            "Failed to remove running messages";
          setLastError(errorMessage);
          return false;
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to remove running messages";
        setLastError(errorMessage);
        return false;
      }
    },
    []
  );

  // Load more running messages
  const loadMore = useCallback(async () => {
    if (!loadingMore && hasMore) {
      const nextPage = currentPage + 1;
      await list(
        filters,
        { page: nextPage, pageSize: pageSizeRef.current },
        sortByRef.current,
        false
      );
    }
  }, [list, loadingMore, hasMore, currentPage, filters]);

  // Refresh running messages
  const refresh = useCallback(async () => {
    await list(
      filters,
      { page: 1, pageSize: pageSizeRef.current },
      sortByRef.current,
      true
    );
  }, [list, filters]);

  // Auto-load effect
  const [hasAutoLoaded, setHasAutoLoaded] = useState(false);

  useEffect(() => {
    if (autoLoad && !hasAutoLoaded) {
      setHasAutoLoaded(true);
      list(initialFilters, { page: 1, pageSize }, initialSort, true);
    }
  }, [autoLoad, hasAutoLoaded, list, pageSize, initialSort]);

  return {
    runningMessage,
    runningMessages,
    loading,
    loadingMore,
    hasMore,
    isEmpty,
    filters,
    sortBy,
    currentPage,
    pageSize,
    isCreating,
    isUpdating,
    removingMessageId,
    selectedMessage,
    get,
    list,
    getMany,
    create,
    update,
    remove,
    removeMany,
    loadMore,
    refresh,
    setFilters,
    setSortBy,
    setSelectedMessage,
    reset,
    inactiveMessages,
    messagesByVenue,
    messagesByStatus,
    lastError,
    clearError,
  };
};
